import { z } from 'zod';

// ---------- Manufacturing - Mattress ----------

export const BOM_QTY_RULES = ['FIXED', 'AREA', 'VOLUME', 'PERIMETER'] as const;
export type BomQtyRule = (typeof BOM_QTY_RULES)[number];
export const BOM_QTY_RULE_LABELS: Record<BomQtyRule, string> = {
  FIXED: 'Fixed per mattress',
  AREA: 'Area — L × W (m²)',
  VOLUME: 'Volume — L × W × thickness (m³)',
  PERIMETER: 'Perimeter — 2 × (L + W) (m)',
};
export type BomStatus = 'DRAFT' | 'APPROVED' | 'SUPERSEDED';

/**
 * Quantity of one BOM line for a mattress size (cm). The same formula runs on the server
 * (requirements, issues) and in the browser (live size preview).
 */
export function bomLineQty(line: { rule: BomQtyRule; factor: number; thicknessCm: number | null; wastagePct: number }, size: { lengthCm: number | null; widthCm: number | null; heightCm: number | null }, override?: number | null): number | null {
  if (override != null) return round6(override);
  const L = (size.lengthCm ?? 0) / 100;
  const W = (size.widthCm ?? 0) / 100;
  const T = (line.thicknessCm ?? size.heightCm ?? 0) / 100;
  let base: number;
  switch (line.rule) {
    case 'FIXED':
      base = 1;
      break;
    case 'AREA':
      if (!L || !W) return null;
      base = L * W;
      break;
    case 'VOLUME':
      if (!L || !W || !T) return null;
      base = L * W * T;
      break;
    case 'PERIMETER':
      if (!L || !W) return null;
      base = 2 * (L + W);
      break;
  }
  return round6(base * line.factor * (1 + (line.wastagePct || 0) / 100));
}
const round6 = (n: number) => Math.round(n * 1e6) / 1e6;

const num = (min: number, max: number) => z.preprocess((v) => (v === '' || v == null ? null : v), z.coerce.number().min(min).max(max).nullable());

export const mtrBomSchema = z
  .object({
    productId: z.string().uuid('Select the mattress model'),
    notes: z.string().trim().max(1000).default(''),
    lines: z
      .array(
        z.object({
          layer: z.string().trim().max(60).default(''),
          itemId: z.string({ error: 'Select a material' }).uuid('Select a material'),
          rule: z.enum(BOM_QTY_RULES),
          factor: z.coerce.number({ error: 'Enter the factor' }).positive('Must be above 0').max(1_000_000),
          thicknessCm: num(0.1, 100).default(null),
          wastagePct: z.preprocess((v) => (v === '' || v == null ? 0 : v), z.coerce.number().min(0, 'Cannot be negative').max(100)).default(0),
          stageId: z.preprocess((v) => (v === '' ? null : v), z.string().uuid().nullable()).default(null),
          operationTypeId: z.preprocess((v) => (v === '' ? null : v), z.string().uuid().nullable()).default(null),
          notes: z.string().trim().max(200).default(''),
          overrides: z.array(z.object({ itemId: z.string().uuid(), qty: z.coerce.number().min(0).max(1_000_000) })).max(500).default([]),
        }),
      )
      .min(1, 'Add at least one material')
      .max(80),
  })
  .superRefine((v, ctx) => {
    v.lines.forEach((l, i) => {
      if (l.rule !== 'VOLUME' && l.thicknessCm != null) ctx.addIssue({ code: 'custom', path: ['lines', i, 'thicknessCm'], message: 'Only for volume lines' });
    });
  });
export type MtrBomInput = z.infer<typeof mtrBomSchema>;

export interface MtrBomListRow {
  id: string;
  product: { id: string; code: string; name: string; category: string };
  revision: number;
  status: BomStatus;
  effectiveFrom: string | null;
  lines: number;
  variants: number;
  approvedBy: string | null;
  updatedAt: string;
}

export interface MtrBomDetail {
  id: string;
  product: { id: string; code: string; name: string; category: string };
  revision: number;
  status: BomStatus;
  effectiveFrom: string | null;
  notes: string;
  approvedBy: string | null;
  approvedAt: string | null;
  version: number;
  lines: { id: string; lineNo: number; layer: string; item: { id: string; code: string; name: string; uom: string; avgCost: number | null }; rule: BomQtyRule; factor: number; thicknessCm: number | null; wastagePct: number; stageId: string | null; operationTypeId: string | null; notes: string; overrides: { itemId: string; qty: number }[] }[];
  /** Sizes of the model (variants) the recipe is applied to. */
  variants: { id: string; code: string; name: string; lengthCm: number | null; widthCm: number | null; heightCm: number | null }[];
  /** Other revisions of the same model. */
  history: { id: string; revision: number; status: BomStatus; effectiveFrom: string | null; approvedBy: string | null }[];
}

export const mtrStageSchema = z.object({
  code: z.string().trim().toUpperCase().regex(/^[A-Z0-9_]{2,12}$/, 'Letters, digits and _ (2–12)'),
  name: z.string().trim().min(2).max(60),
  seq: z.coerce.number().int().min(0).max(999),
  /** Production section of the stage. */
  sectionId: z.preprocess((v) => (v === '' ? null : v), z.string().uuid().nullable()).default(null),
  isQc: z.boolean().default(false),
  isFinal: z.boolean().default(false),
  isActive: z.boolean().default(true),
});
export type MtrStageInput = z.infer<typeof mtrStageSchema>;
export interface MtrStageItem extends MtrStageInput {
  id: string;
  department: string | null;
  version: number;
}

// ---------- Demand, plan & production orders ----------

export interface MtrDemandLine {
  soLineId: string;
  orderId: string;
  orderNumber: string | null;
  customer: string | null;
  orderDate: string;
  committedDate: string | null;
  item: { id: string; code: string; name: string };
  model: string;
  ordered: number;
  delivered: number;
  planned: number;
  /** Still to plan into production. */
  toPlan: number;
  fgOnHand: number;
  hasBom: boolean;
}

export interface MtrStockSuggestion {
  item: { id: string; code: string; name: string };
  model: string;
  onHand: number;
  plannedForStock: number;
  minQty: number;
  maxQty: number | null;
  suggest: number;
  hasBom: boolean;
}

export const mtrOrderSchema = z.object({
  docDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  plannedDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter the production date'),
  rmWarehouseId: z.string().uuid('Select the raw material store'),
  fgWarehouseId: z.string().uuid('Select the finished goods warehouse'),
  notes: z.string().trim().max(1000).default(''),
  lines: z
    .array(
      z.object({
        itemId: z.string({ error: 'Select a mattress size' }).uuid('Select a mattress size'),
        qty: z.coerce.number({ error: 'Enter the quantity' }).positive('Must be above 0').max(100000),
        soLineId: z.preprocess((v) => (v === '' ? null : v), z.string().uuid().nullable()).default(null),
        notes: z.string().trim().max(200).default(''),
      }),
    )
    .min(1, 'Add at least one mattress')
    .max(300),
});
export type MtrOrderInput = z.infer<typeof mtrOrderSchema>;

export interface MtrOrderListRow {
  id: string;
  number: string | null;
  status: import('./documents').DocStatus;
  docDate: string;
  plannedDate: string;
  lines: number;
  units: number;
  produced: number;
  rejected: number;
  forOrders: number;
  forStock: number;
  customers: string[];
}

export interface MtrOrderDetail {
  id: string;
  number: string | null;
  status: import('./documents').DocStatus;
  docDate: string;
  plannedDate: string;
  rmWarehouseId: string;
  fgWarehouseId: string;
  notes: string;
  startedAt: string | null;
  completedAt: string | null;
  createdBy: string | null;
  version: number;
  lines: {
    id: string;
    lineNo: number;
    item: { id: string; code: string; name: string; lengthCm: number | null; widthCm: number | null; heightCm: number | null };
    qty: number;
    produced: number;
    rejected: number;
    soLine: { id: string; orderId: string; orderNumber: string | null; customer: string | null; committedDate: string | null } | null;
    bomRevision: number | null;
    hasBom: boolean;
    notes: string;
  }[];
}

export interface MtrRequirementRow {
  item: { id: string; code: string; name: string; uom: string };
  required: number;
  issued: number;
  toIssue: number;
  onHand: number;
  short: number;
  /** Issued above the BOM (additional material). */
  extra: number;
  avgCost: number | null;
}

export interface MtrRequirement {
  rows: MtrRequirementRow[];
  missingBom: { itemId: string; name: string }[];
  issues: { id: string; number: string | null; status: import('./documents').DocStatus; docType: string }[];
  requisitions: { id: string; number: string | null; status: import('./documents').DocStatus }[];
}

export interface MtrPlanDay {
  date: string;
  units: number;
  orders: { id: string; number: string | null; status: import('./documents').DocStatus; units: number; produced: number; lines: number; customers: string[] }[];
}

// ---------- Production entries (data-entry operator) ----------

const qtyField = z.preprocess((v) => (v === '' || v == null ? 0 : v), z.coerce.number().min(0, 'Cannot be negative').max(100000));
export const mtrEntrySchema = z.object({
  docDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  orderId: z.string().uuid('Select the production order'),
  shift: z.string().trim().max(30).default(''),
  notes: z.string().trim().max(1000).default(''),
  lines: z
    .array(
      z
        .object({
          orderLineId: z.string().uuid(),
          stageId: z.string().uuid(),
          good: qtyField,
          rework: qtyField,
          rejected: qtyField,
          reason: z.string().trim().max(200).default(''),
          employeeIds: z.array(z.string().uuid()).max(20).default([]),
          startTime: z.preprocess((v) => (v === '' ? null : v), z.string().regex(/^\d{2}:\d{2}$/, 'HH:MM').nullable()).default(null),
          endTime: z.preprocess((v) => (v === '' ? null : v), z.string().regex(/^\d{2}:\d{2}$/, 'HH:MM').nullable()).default(null),
        })
        .refine((l) => l.rejected === 0 || l.reason.length >= 3, { path: ['reason'], message: 'Give the reason for rejects' }),
    )
    .max(500),
});
export type MtrEntryInput = z.infer<typeof mtrEntrySchema>;

/** Cumulative output of one size at one stage (posted entries; pending shown apart). */
export interface MtrStageProgress {
  stageId: string;
  good: number;
  rework: number;
  rejected: number;
  pendingGood: number;
  /** What can still be recorded as good here: previous stage's good (or the line quantity) − this stage's good − rejected. */
  available: number;
}

export interface MtrOrderProgress {
  orderId: string;
  stages: { id: string; code: string; name: string; isQc: boolean; isFinal: boolean }[];
  lines: { orderLineId: string; item: { id: string; name: string }; qty: number; produced: number; rejected: number; stages: MtrStageProgress[] }[];
}

export interface MtrEntryListRow {
  id: string;
  number: string | null;
  status: import('./documents').DocStatus;
  docDate: string;
  shift: string;
  order: { id: string; number: string | null };
  good: number;
  rejected: number;
  finalGood: number;
  createdBy: string | null;
}

export interface MtrEntryDetail {
  id: string;
  number: string | null;
  status: import('./documents').DocStatus;
  docDate: string;
  orderId: string;
  orderNumber: string | null;
  shift: string;
  notes: string;
  fgReceipt: { id: string; number: string | null } | null;
  serials: string[];
  version: number;
  lines: (MtrEntryInput['lines'][number] & { id: string })[];
}

export interface MtrMissingEntry {
  order: { id: string; number: string | null; plannedDate: string };
  units: number;
  produced: number;
  lastEntry: string | null;
  daysSince: number | null;
}
